import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, KeyRound, Lock, ArrowLeft, Send } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const field = "bg-brand-obsidian border-brand-line pl-10";

// Step 1: enter email -> a 6-digit code is emailed. Step 2: enter code + new password.
export default function AdminResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message);
      setStep(2);
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("The two passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email, code, password });
      navigate("/admin/login?reset=1", { replace: true });
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-brand-obsidian flex items-center justify-center px-4 grain relative" data-testid="admin-reset-page">
      <Link to="/admin/login" data-testid="admin-reset-back" className="absolute top-6 left-6 inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to login
      </Link>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-display text-2xl font-bold text-white">Reset your password</h1>
          <p className="mt-1 text-sm text-white/50">
            {step === 1 ? "Enter the email you log in with and we'll email you a 6-digit reset code." : "Check your inbox (and spam folder) for the 6-digit code."}
          </p>
        </div>

        {step === 1 ? (
          <form onSubmit={requestCode} className="rounded-lg border border-brand-line bg-brand-surface p-8 space-y-5" data-testid="admin-forgot-form">
            <div className="space-y-2">
              <Label htmlFor="r-email" className="text-white/80 text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input id="r-email" type="email" required data-testid="admin-forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} />
              </div>
            </div>
            {error && <p className="text-sm text-red-400" data-testid="admin-reset-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="admin-forgot-submit" className="w-full bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow uppercase tracking-[0.2em] text-xs font-bold py-6 rounded-md">
              {busy ? "Sending..." : "Email me a reset code"} <Send className="h-4 w-4" />
            </Button>
          </form>
        ) : (
          <form onSubmit={reset} className="rounded-lg border border-brand-line bg-brand-surface p-8 space-y-5" data-testid="admin-reset-form">
            {msg && <p className="text-sm text-white/70" data-testid="admin-reset-sent">{msg}</p>}
            <div className="space-y-2">
              <Label htmlFor="r-code" className="text-white/80 text-sm">6-digit code</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input id="r-code" inputMode="numeric" autoComplete="one-time-code" required data-testid="admin-reset-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className={field} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="r-pw" className="text-white/80 text-sm">New password (min 8 characters)</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input id="r-pw" type="password" autoComplete="new-password" required minLength={8} data-testid="admin-reset-password" value={password} onChange={(e) => setPassword(e.target.value)} className={field} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="r-pw2" className="text-white/80 text-sm">Confirm new password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input id="r-pw2" type="password" autoComplete="new-password" required minLength={8} data-testid="admin-reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={field} />
              </div>
            </div>
            {error && <p className="text-sm text-red-400" data-testid="admin-reset-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="admin-reset-submit" className="w-full bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow uppercase tracking-[0.2em] text-xs font-bold py-6 rounded-md">
              {busy ? "Saving..." : "Set new password"}
            </Button>
            <button type="button" onClick={() => { setStep(1); setError(""); }} data-testid="admin-reset-resend" className="w-full text-xs text-white/50 hover:text-white">
              Didn't get a code? Send another
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
